/**
 * Collection Carousel - JavaScript
 * Handles Swiper initialization and responsive behavior
 */

(function() {
  'use strict';

  // Store Swiper instances
  const swiperInstances = new Map();

  /**
   * Initialize a collection carousel
   */
  function initCollectionCarousel(sectionElement) {
    const sectionId = sectionElement.dataset.sectionId;

    // CRITICAL: Check if already initialized on this DOM element
    if (sectionElement._collectionCarouselInitialized) {
      console.log(`[Collection Carousel] Already initialized on DOM element - skipping`);
      return;
    }

    const carouselElement = sectionElement.querySelector('[data-collection-swiper]');
    if (!carouselElement) {
      console.warn('[Collection Carousel] Swiper element not found');
      return;
    }

    const carouselKey = `${sectionId}-collection-carousel`;

    // Check if already in instances map
    if (swiperInstances.has(carouselKey)) {
      console.log(`[Collection Carousel] Already in instances map: ${carouselKey} - skipping`);
      return swiperInstances.get(carouselKey);
    }

    // Count total slides
    const totalSlides = carouselElement.querySelectorAll('.swiper-slide').length;
    console.log(`[Collection Carousel] Initializing with ${totalSlides} slides`);

    // Swiper configuration with responsive breakpoints
    const swiperConfig = {
      // Responsive breakpoints
      breakpoints: {
        // Mobile (<768px): 2.3 slides, slide 2 at a time
        320: {
          slidesPerView: 2.3,
          slidesPerGroup: 2,  // Slide 2 cards per click
          spaceBetween: 12,
          centeredSlides: false,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0
        },
        // Tablet (768px - 1024px): 2.5 slides, slide 2 at a time
        768: {
          slidesPerView: 2.5,
          slidesPerGroup: 2,  // Slide 2 cards per click
          spaceBetween: 12,
          centeredSlides: false
        },
        // laptop (>1024px): 4.2 slides, slide 3 at a time
        1025: {
          slidesPerView: 4.2,
          slidesPerGroup: 3,  // Slide 3 cards per click
          spaceBetween: 16,
          centeredSlides: false
        },
        // laptop (>1400px): 4.5 slides, slide 3 at a time
        1400: {
          slidesPerView: 4.5,
          slidesPerGroup: 3,  // Slide 3 cards per click
          spaceBetween: 16,
          centeredSlides: false
        },
        // Desktop (>1600px): 4.5 slides, slide 3 at a time
        1600: {
          slidesPerView: 4.5,
          slidesPerGroup: 3,  // Slide 3 cards per click
          spaceBetween: 16,
          centeredSlides: false
        }
      },

      // Performance & tracking
      watchSlidesProgress: true,
      watchSlidesVisibility: true,  // Enable visibility tracking for smoother performance
      observer: true,
      observeParents: true,

      // Touch & drag settings (matching collection-tabs-swiper for consistency)
      simulateTouch: true,
      allowTouchMove: true,
      touchRatio: 1,
      resistance: true,
      resistanceRatio: 0.85,
      threshold: 5,  // 5px threshold prevents accidental clicks during swipe
      followFinger: true,
      shortSwipes: true,
      longSwipes: true,

      // CRITICAL: Fix for dragging in gaps between later slides
      // Default 'wrapper' only works on initially visible slides
      // See: https://github.com/nolimits4web/swiper/issues/4942
      touchEventsTarget: 'container',

      // Layout
      centeredSlides: false,
      updateOnWindowResize: true,
      resizeObserver: true,

      // Navigation
      navigation: {
        nextEl: carouselElement.querySelector('.swiper-button-next'),
        prevEl: carouselElement.querySelector('.swiper-button-prev')
      },

      // Lazy loading
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2
      },

      // Transitions
      speed: 600,
      cssMode: false,

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous collection',
        nextSlideMessage: 'Next collection'
      },

      // Event callbacks
      on: {
        init: function() {
          console.log(`[Collection Carousel] Swiper initialized: ${carouselKey}`);
          updateNavigationButtonVisibility(this, carouselElement, totalSlides);
        },
        breakpoint: function(swiper, breakpointParams) {
          console.log(`[Collection Carousel] Breakpoint changed:`, breakpointParams);
          updateNavigationButtonVisibility(swiper, carouselElement, totalSlides);
          // Re-equalize heights when breakpoint changes
          equalizeCardHeights(carouselElement);
        },
        resize: function() {
          console.log(`[Collection Carousel] Window resized`);
          updateNavigationButtonVisibility(this, carouselElement, totalSlides);
          // Re-equalize heights on resize
          equalizeCardHeights(carouselElement);
        }
      }
    };

    // Initialize Swiper
    const collectionSwiper = new Swiper(carouselElement, swiperConfig);
    swiperInstances.set(carouselKey, collectionSwiper);

    // CRITICAL: Mark this section as initialized on the DOM element
    sectionElement._collectionCarouselInitialized = true;

    console.log(`[Collection Carousel] Instance created: ${carouselKey}`);

    // Equalize card heights if any title wraps to 2 lines
    equalizeCardHeights(carouselElement);
  }

  /**
   * Equalize card heights based on tallest card
   * Only applies uniform height if any title wraps to 2 lines
   */
  function equalizeCardHeights(carouselElement) {
    // Wait for fonts to load to get accurate measurements
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        performHeightEqualization(carouselElement);
      });
    } else {
      // Fallback for browsers without Font Loading API
      requestAnimationFrame(() => {
        performHeightEqualization(carouselElement);
      });
    }
  }

  /**
   * Perform the actual height equalization
   */
  function performHeightEqualization(carouselElement) {
    const cards = carouselElement.querySelectorAll('.collection-card');
    if (!cards.length) return;

    // Reset any previous height settings to get natural heights
    cards.forEach(card => {
      card.style.minHeight = '';
    });

    // Force layout recalculation
    carouselElement.offsetHeight;

    // Find the tallest card (naturally)
    let maxHeight = 0;
    cards.forEach(card => {
      const height = card.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    // Only apply uniform height if there's variation (meaning some titles are 2 lines)
    if (maxHeight > 0) {
      cards.forEach(card => {
        card.style.minHeight = `${maxHeight}px`;
      });
      console.log(`[Collection Carousel] Equalized card heights to ${maxHeight}px`);
    }
  }

  /**
   * Update navigation button visibility
   * Hide buttons when all items fit in viewport
   */
  function updateNavigationButtonVisibility(swiperInstance, carouselElement, totalSlides) {
    const prevBtn = carouselElement.querySelector('.swiper-button-prev');
    const nextBtn = carouselElement.querySelector('.swiper-button-next');

    if (!prevBtn || !nextBtn || !swiperInstance) return;

    // Get current slidesPerView based on viewport
    const currentSlidesPerView = swiperInstance.params.slidesPerView;

    console.log(`[Collection Carousel Nav] Total: ${totalSlides}, SlidesPerView: ${currentSlidesPerView}`);

    // Hide buttons if all slides fit in view
    // Use floor() because 3.2 means only 3 slides are fully visible
    if (totalSlides <= Math.floor(currentSlidesPerView)) {
      prevBtn.style.setProperty('display', 'none', 'important');
      nextBtn.style.setProperty('display', 'none', 'important');
      console.log(`[Collection Carousel Nav] Hidden - all items visible`);
    } else {
      prevBtn.style.removeProperty('display');
      nextBtn.style.removeProperty('display');
      console.log(`[Collection Carousel Nav] Shown - scrolling needed`);
    }
  }

  /**
   * Initialize all collection carousels on the page
   */
  function initAllCarousels() {
    const sections = document.querySelectorAll('[data-section-type="collection-carousel"]');
    console.log(`[Collection Carousel] Found ${sections.length} section(s)`);

    sections.forEach(section => {
      initCollectionCarousel(section);
    });
  }

  /**
   * Cleanup function for Shopify theme editor
   */
  function cleanupCarousels() {
    swiperInstances.forEach((swiper, key) => {
      // CRITICAL: Only destroy instance, don't clean styles (avoid layout breaks)
      swiper.destroy(true, false);
      console.log(`[Collection Carousel] Destroyed: ${key}`);
    });
    swiperInstances.clear();

    // Clear all initialization flags
    const sections = document.querySelectorAll('[data-section-type="collection-carousel"]');
    sections.forEach(section => {
      section._collectionCarouselInitialized = false;
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCarousels);
  } else {
    initAllCarousels();
  }

  // Shopify theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('[data-section-type="collection-carousel"]');
      if (section) {
        console.log('[Collection Carousel] Section loaded in theme editor');
        initCollectionCarousel(section);
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('[data-section-type="collection-carousel"]');
      if (section) {
        const sectionId = section.dataset.sectionId;
        const carouselKey = `${sectionId}-collection-carousel`;

        if (swiperInstances.has(carouselKey)) {
          const swiper = swiperInstances.get(carouselKey);
          // CRITICAL: Only destroy instance, don't clean styles
          swiper.destroy(true, false);
          swiperInstances.delete(carouselKey);
          // Clear the initialization flag
          section._collectionCarouselInitialized = false;
          console.log(`[Collection Carousel] Section unloaded: ${carouselKey}`);
        }
      }
    });

    document.addEventListener('shopify:section:reorder', function() {
      console.log('[Collection Carousel] Sections reordered');
      cleanupCarousels();
      initAllCarousels();
    });
  }

  // Handle window resize (debounced)
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      swiperInstances.forEach((swiper, key) => {
        if (swiper && swiper.update) {
          swiper.update();
          // Re-equalize card heights after resize
          const section = document.querySelector(`[data-section-id="${key.replace('-collection-carousel', '')}"]`);
          if (section) {
            const carouselElement = section.querySelector('[data-collection-swiper]');
            if (carouselElement) {
              equalizeCardHeights(carouselElement);
            }
          }
        }
      });
    }, 250);
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanupCarousels);

})();
